/**
 * A formatação de uma petição do advogado (Word ou PDF), trazida para a peça.
 * O editor não lê o arquivo: quem mede é o app (`extrairFormatacao`), e o
 * editor só mostra o que foi medido e aplica.
 */
import { PAPEIS, type Margens, type Orientacao, type Papel, type Timbrado } from './documento';
import { fmtNum, type FormatoParagrafo } from './estilos';

export interface ExtracaoDeArquivo {
  /** Só o que foi medido: o resto fica como está no estilo. */
  formato: Partial<FormatoParagrafo>;
  /** Cada margem por si: no PDF a inferior não se mede. */
  documento: { margens?: Partial<Margens>; papel?: Papel; orientacao?: Orientacao };
  /** O que foi lido mas não se aplica (ex.: a fonte que o editor não tem). */
  avisos: string[];
  /**
   * O cabeçalho e o rodapé do arquivo: o texto (sem o número), se numera as
   * páginas e o desenho — a faixa pronta de cada borda (`data:`), ou o
   * logotipo quando o cabeçalho também tem texto.
   */
  timbrado?: {
    cabecalho: string;
    rodape: string;
    numeroDePagina: boolean;
    imagemCabecalho?: string;
    imagemRodape?: string;
    logotipo?: string;
  };
}

/** O arquivo traz um cabeçalho ou um rodapé (texto ou desenho) que pode virar o timbrado. */
export function temTimbrado(x: ExtracaoDeArquivo): boolean {
  const t = x.timbrado;
  return !!(t?.cabecalho || t?.rodape || t?.imagemCabecalho || t?.imagemRodape);
}

/**
 * O timbrado que o arquivo monta, no lugar do `anterior` (o guardado da pessoa,
 * de quem herda o id e o logotipo, quando o arquivo não traz um). Com uma faixa
 * pronta, é o modo `imagem`; senão, texto. O logotipo fica guardado mesmo com a
 * faixa no cabeçalho: ela só passa na frente dele (tirando a faixa, ele volta).
 */
export function timbradoDoArquivo(x: ExtracaoDeArquivo, anterior: Timbrado | null, idNovo: string): Timbrado | null {
  const t = x.timbrado;
  if (!t || !temTimbrado(x)) return null;
  const faixa = !!(t.imagemCabecalho || t.imagemRodape);
  return {
    id: anterior?.id ?? idNovo,
    rotulo: 'Meu timbrado',
    modo: faixa ? 'imagem' : 'texto',
    cabecalhoTexto: t.cabecalho,
    rodapeTexto: t.rodape,
    imagemCabecalho: t.imagemCabecalho,
    imagemRodape: t.imagemRodape,
    logotipo: t.logotipo ?? anterior?.logotipo,
  };
}

const ALINHAMENTO: Record<string, string> = { left: 'à esquerda', center: 'centralizado', right: 'à direita', justify: 'justificado' };
const MARGENS: [keyof Margens, string][] = [['superior', 'sup.'], ['inferior', 'inf.'], ['esquerda', 'esq.'], ['direita', 'dir.']];

/** As linhas ("Rótulo: valor") que o painel mostra do que veio do arquivo; vazio = nada a aplicar. */
export function resumoDaFormatacao(x: ExtracaoDeArquivo): string[] {
  const f = x.formato;
  const linhas: string[] = [];
  if (f.fonte || f.tamanho) linhas.push(`Fonte: ${[f.fonte, f.tamanho ? `${fmtNum(f.tamanho)} pt` : null].filter(Boolean).join(' ')}`);
  if (f.alinhamento) linhas.push(`Alinhamento: ${ALINHAMENTO[f.alinhamento]}`);
  if (f.entrelinha) linhas.push(`Entrelinha: ${fmtNum(f.entrelinha)}`);
  if (f.especial === 'primeira') linhas.push(`Recuo da 1ª linha: ${fmtNum(f.medida ?? 0)} cm`);
  else if (f.especial === 'deslocado') linhas.push(`Recuo deslocado: ${fmtNum(f.medida ?? 0)} cm`);
  else if (f.especial === 'nenhum') linhas.push('Recuo da 1ª linha: nenhum');
  if (f.recuoEsquerdo !== undefined || f.recuoDireito !== undefined) linhas.push(`Recuos: ${fmtNum(f.recuoEsquerdo ?? 0)} cm à esquerda, ${fmtNum(f.recuoDireito ?? 0)} cm à direita`);
  if (f.espacoAntes !== undefined || f.espacoDepois !== undefined) {
    linhas.push(`Espaço antes / depois: ${fmtNum(f.espacoAntes ?? 0)} / ${fmtNum(f.espacoDepois ?? 0)} pt`);
  }
  const m = x.documento.margens ?? {};
  const medidas = MARGENS.filter(([k]) => m[k] !== undefined).map(([k, nome]) => `${nome} ${fmtNum(m[k]!)}`);
  if (medidas.length) linhas.push(`Margens: ${medidas.join(', ')} cm`);
  const { papel, orientacao } = x.documento;
  if (papel || orientacao) linhas.push(`Papel: ${[papel && PAPEIS[papel].nome, orientacao].filter(Boolean).join(', ')}`);
  if (x.timbrado?.numeroDePagina) linhas.push('Páginas: numeradas');
  return linhas;
}
